import { api, ensureCsrf } from './client';
import type { AxiosProgressEvent } from 'axios';

/** Default endpoint backed by App\Http\Controllers\UploadController@store. */
export const UPLOAD_ENDPOINT = '/api/v1/uploads';

/**
 * Metadata returned by POST /api/v1/uploads for each uploaded file.
 * `path` is the canonical value we persist (e.g. `uploads/2026/08/ab12.jpg`);
 * `url` is the browsable location used for previews.
 */
export interface UploadedFileMeta {
  path: string;
  url: string;
  mime: string;
  size: number;
  name?: string;
}

/**
 * The API wraps every payload in a `{ data: ... }` envelope (see
 * useResourceList/Show/Create in ./hooks). We accept the unwrapped shape too so
 * a future interceptor that flattens responses can't silently break uploads.
 */
type UploadResponse = { data?: UploadedFileMeta } & Partial<UploadedFileMeta>;

export interface UploadFileOptions {
  /** Overrides the endpoint. Defaults to /api/v1/uploads. */
  endpoint?: string;
  /** Called with 0-100 as the request body streams out. */
  onProgress?: (percent: number) => void;
  /** Lets the caller abort an in-flight upload. */
  signal?: AbortSignal;
}

/**
 * Uploads a single file and returns its server-issued metadata.
 * Throws if the response carries no usable path.
 */
export async function uploadFile(
  file: File,
  { endpoint = UPLOAD_ENDPOINT, onProgress, signal }: UploadFileOptions = {},
): Promise<UploadedFileMeta> {
  await ensureCsrf();

  const form = new FormData();
  form.append('file', file);

  const { data } = await api.post<UploadResponse>(endpoint, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
    signal,
    onUploadProgress: onProgress
      ? (evt: AxiosProgressEvent) => {
          if (!evt.total) return;
          onProgress(Math.round((evt.loaded / evt.total) * 100));
        }
      : undefined,
  });

  const meta = data?.data ?? (data as UploadedFileMeta | undefined);
  if (!meta?.path) {
    throw new Error('Upload succeeded but the server returned no file path.');
  }
  return meta;
}

/**
 * Turns a stored value into something an <img src> can load.
 *
 * We persist the disk-relative path the API returns (`uploads/2026/08/x.jpg`)
 * rather than an absolute URL, so the record stays portable across hosts. That
 * path is not directly browsable — files live on Laravel's `public` disk, which
 * is served from `/storage`. This maps one to the other, and passes through
 * anything already absolute (legacy rows that stored a full URL, blob previews,
 * data URIs).
 */
export function resolveUploadUrl(value?: string | null): string {
  if (!value) return '';
  const trimmed = value.trim();
  if (!trimmed) return '';
  if (/^(https?:\/\/|blob:|data:|\/\/)/i.test(trimmed)) return trimmed;
  if (trimmed.startsWith('/')) return trimmed;
  return `/storage/${trimmed.replace(/^\/+/, '')}`;
}
